import { FC, useEffect, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Models
import IContact from 'app/models/Contact';
// Selectors
import { selectContacts, selectParams, selectTotal } from 'app/store/Contacts/Contacts.selectors';
// Mui
import { Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
// Components
import { Loader, Message } from 'app/components/Utilities';
// 
import ContactsListItem from './ContactsListItem';
import { getContacts } from 'app/store/Contacts/Contacts.async';

const ContactsList:FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  // State
  const contacts = useAppSelector(selectContacts);
  const params = useAppSelector(selectParams);
  const total = useAppSelector(selectTotal);

  const [limit, setLimit] = useState<number>(params.limit);
  const [offset, setOffset] = useState<number>(params.offset);

  const handleChangePage = (_: unknown, newPage: number) => {
    const nextOffset = newPage * limit;
    setOffset(nextOffset);
    dispatch(getContacts({ ...params, limit, offset: nextOffset }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextLimit = parseInt(event.target.value, 10);
    setLimit(nextLimit);
    setOffset(0);
    dispatch(getContacts({ ...params, limit: nextLimit, offset: 0 }));
  };

  useEffect(() => {
    setOffset(params.offset);
  }, [params.offset]);

  if ( !contacts ) return <Loader />;
  if ( !contacts.length ) return <Message text={t('pages.contacts.noData')} />;
  return (
    <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <TableContainer sx={{ flexGrow: 1, height: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('labels.name')}</TableCell>
              <TableCell>{t('labels.email')}</TableCell>
              <TableCell>{t('labels.phone')}</TableCell>
              <TableCell>{t('labels.address')}</TableCell>
              <TableCell align='right'>{t('labels.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact:IContact) => (
              <ContactsListItem
                key={`contact-item-${contact.id}`}
                contact={contact}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <TablePagination
        component="div"
        sx={{ flexShrink: 0 }}
        count={total}
        page={Math.floor(offset / limit)}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
      />
    </Paper>
  );
}

export default ContactsList;
